import parse from "html-dom-parser";
import { cleanSectionHtml } from "functions/TemplateEditor";

import { DEFAULT_PAGE_STYLE, CONTEXT_MENU_ACTIONS } from "../Constants";
import { isArray } from "lodash";

/**
 * @description The `generateTemplateJSON` function takes a template as input, extracts data from the DOM, and
 * generates a JSON representation of the template.
 * @param template - Template json
 * @returns a JSON string representation of the template object with the updated templateContent.pages
 * property.
 */
export function generateTemplateJSON(template) {
  try {
    // get pages
    const pages = [];
    const pageElements = document.querySelectorAll("[id=divUSCPageContent]");

    if (pageElements && pageElements.length > 0) {
      // iterate pages
      pageElements.forEach((page, index) => {
        // get sections
        const sectionsElements = page.querySelectorAll("div[sectionname]");
        const sectionsData = Array.from(sectionsElements)
          ?.filter((element) => !element?.getAttribute("deleted"))
          .map((element) => {
            const boundingRect = element.getBoundingClientRect();
            const obj = {
              ruleName: "",
              name: element.getAttribute("sectionname") ?? "",
              width: boundingRect?.width ?? 0,
              height: boundingRect?.height ?? 0,
              rule: element.getAttribute("rule") ?? "",
              isDefault: Boolean(element.getAttribute("defaultsection")),
              repeatingAdapterId: 0,
              isRepeating: false,
              repeatingContent: null,
              locked: Boolean(element.getAttribute("locked")),
            };

            const translate = parseComplexStyleProperty(
              element.style.transform
            );

            const sectionElement = element.cloneNode(true);

            sectionElement.style.transform = `translate(0px, 0px)`;
            const left = parseInt(element.style.left) + translate.x;
            const top = parseInt(element.style.top) + translate.y;
            sectionElement.style.left = left + "px";
            sectionElement.style.top = top + "px";

            sectionElement.removeAttribute("class");
            sectionElement.removeAttribute("sectionname");
            sectionElement.removeAttribute("rule");
            sectionElement.removeAttribute("id");
            sectionElement.removeAttribute("hidden");
            sectionElement.removeAttribute("defaultsection");

            const contentHtml = sectionElement.querySelector(
              ".usc-section-content"
            );

            if (contentHtml) {
              // clean unwanted content from the html content
              cleanHtmlTemplateContent();

              // handle and generate repeatable table content
              const repeatableTable = contentHtml?.querySelector(
                `table[id="repeatableTable"]`
              );
              if (repeatableTable) {
                const repeatingRow = repeatableTable.querySelector(
                  "tr[id=repeatingcontent]"
                );

                let repeatingContent = "",
                  repeatingAdapterId;
                if (repeatingRow) {
                  const params = repeatingRow.querySelectorAll("params");
                  params.forEach((param, index) => {
                    param.removeAttribute("isrepeating");
                  });

                  repeatingAdapterId = parseInt(
                    repeatingRow.getAttribute("repeatingadapterid") ?? 0
                  );
                  repeatingRow.removeAttribute("repeatingadapterid");
                  repeatingContent = _.cloneDeep(repeatingRow.outerHTML);

                  // remove repeatable row before get the content
                  const emptyRow = document.createElement("tr");
                  emptyRow.setAttribute("id", "repeatingcontent");
                  repeatingRow.replaceWith(emptyRow);
                  sectionElement.innerHTML = contentHtml.innerHTML;
                }
                repeatableTable.removeAttribute("id");
                repeatableTable.removeAttribute("repeatable");
                repeatableTable.removeAttribute("repeatingadapterid");

                return {
                  ...obj,
                  content: cleanSectionHtml(sectionElement.outerHTML),
                  repeatingContent,
                  isRepeating: true,
                  repeatingAdapterId,
                };
              } else {
                sectionElement.innerHTML = contentHtml.innerHTML;
                return {
                  ...obj,
                  content: cleanSectionHtml(sectionElement.outerHTML),
                };
              }
            } else {
              return {
                ...obj,
                content: cleanSectionHtml(element.outerHTML),
              };
            }
          });
        // grouped by template name
        const groupedSectionsData = _.groupBy(sectionsData, "name");
        const sections = [];
        for (const [key, value] of Object.entries(groupedSectionsData)) {
          const section = sectionsData.find((section) => section.name == key);
          if (section) {
            const rules = value.map((rule) =>
              _.omit(rule, "name", "width", "height", "locked")
            );
            sections.push({
              name: key,
              rules: rules,
              width: section?.width,
              height: section?.height,
              locked: section?.locked,
            });
          }
        }
        // prevent duplicates pages adding
        if (!pages.some((item) => item.id == page.getAttribute("page"))) {
          // update pages array
          pages.push({
            sections,
            bgImage: "",
            id: page.getAttribute("page"),
          });
        }
      });

      // remove empty pages and unnecessary attributes
      const cleanedPages = pages
        .map((pageItem) => {
          if (pageItem.sections.length > 0) {
            return _.omit(pageItem, "id");
          }
        })
        ?.filter((page) => page);

      // return template json
      const templateJson = {
        ...template,
        templateContent: {
          ...template.templateContent,
          pages: [...cleanedPages],
        },
      };
      return JSON.stringify(templateJson);
    }
    return null;
  } catch (e) {
    console.error("Error in generateTemplateJSON : ", e);
    return null;
  }
}

/**
 * @description - This function will remove unwanted elements from the html template content
 */
function cleanHtmlTemplateContent() {
  // remove cell menu elements
  const cellMenus = document.getElementsByClassName("usc-table-cell-menu");

  if (cellMenus && cellMenus.length > 0) {
    for (var i = cellMenus.length - 1; i >= 0; i--) {
      var cellMenu = cellMenus[i];
      cellMenu.parentNode.removeChild(cellMenu);
    }
  }

  // remove column resize elements
  const colResizeElements =
    document.getElementsByClassName("table-col-resizer");
  if (colResizeElements && colResizeElements.length > 0) {
    for (var i = colResizeElements.length - 1; i >= 0; i--) {
      var colResizeElement = colResizeElements[i];
      colResizeElement.parentNode.removeChild(colResizeElement);
    }
  }
}

/**
 * @description The `parseComplexStyleProperty` function parses a complex style property string and extracts the
 * translate values.
 * @param str - Style string
 * @returns an object with two properties: x and y. These properties represent the cumulative
 * translation values parsed from the input string.
 */
export function parseComplexStyleProperty(str) {
  let translateXY = { x: 0, y: 0 };
  try {
    var regex = /(\w+)\((.+?)\)/g,
      transform = {},
      match;

    while ((match = regex.exec(str)))
      transform[match[1]] = transform[match[1]]
        ? transform[match[1]].concat([match[2]])
        : [match[2]];
    if (
      transform.hasOwnProperty("translate") &&
      Array.isArray(transform.translate) &&
      transform.translate.length > 0
    ) {
      transform.translate.map((translate) => {
        const obj = translate.split(",");
        translateXY.x += parseInt(obj[0].trim());
        translateXY.y += parseInt(obj[1].trim());
      });
    }
    return translateXY;
  } catch (error) {
    return translateXY;
  }
}

/**
 * @description The `generateTemplateHtml` function generates an HTML document with sections and pages based on the
 * provided template content and drop position.
 * @param templateContent - Template content JSON object
 * @returns The function `generateTemplateHtml` returns the body of an HTML document.
 */
export function generateTemplateHtml(templateContent) {
  const DomParser = new DOMParser();

  /**
   * @description creare HTML doc
   */
  const doc = document.implementation.createHTMLDocument("USCTemplate");

  try {
    const { pages, margin, isMarginApplied } = templateContent ?? {};
    pages.map((page, pageIndex) => {
      let pageSize = document
        .querySelector(".editor-page")
        ?.getBoundingClientRect();
      if (isMarginApplied && margin && pageSize) {
        pageSize.width = pageSize.width - (margin.left + margin.left);
        pageSize.height = pageSize.height - (margin.top + margin.bottom);
      }
      // create new page
      const pageDiv = document.createElement("div");
      pageDiv.setAttribute("id", "divUSCPageContent");
      pageDiv.setAttribute("page", `page_${pageIndex}`);
      pageDiv.setAttribute("style", page.style ?? DEFAULT_PAGE_STYLE);
      pageDiv.setAttribute("class", "editor-page-content");
      pageDiv.style.width = `${pageSize?.width}px`;
      pageDiv.style.height = `${pageSize?.height}px`;

      // create pages
      page.sections.map((section, sectionIndex) => {
        // attach sections to page
        section.rules.map((ruleSection, ruleIndex) => {
          if (ruleSection.content) {
            const node = DomParser.parseFromString(
              ruleSection.content,
              "text/html"
            ).body.firstChild;
            if (node) {
              if (node.firstChild) {
                const tables =
                  node.firstChild.parentElement.querySelectorAll("table");
                tables.forEach((table, index) => {
                  if (table) {
                    // handling repeating parameters
                    if (ruleSection.isRepeating) {
                      const replaceableElement = table.querySelector(
                        "tr[id=repeatingcontent]"
                      );
                      if (replaceableElement) {
                        table.setAttribute("repeatable", "true");
                        table.setAttribute("id", "repeatableTable");
                        table.setAttribute(
                          "repeatingadapterid",
                          ruleSection.repeatingAdapterId
                        );

                        const tempElement = document.createElement("table");
                        tempElement.insertAdjacentHTML(
                          "beforeend",
                          ruleSection.repeatingContent
                        );
                        const repeatableContent =
                          tempElement.querySelector("tr");
                        if (repeatableContent) {
                          repeatableContent.setAttribute(
                            "repeatingadapterid",
                            ruleSection.repeatingAdapterId
                          );
                          const params =
                            repeatableContent.querySelectorAll("params");
                          params.forEach((param, index) => {
                            param.setAttribute("isrepeating", "true");
                          });
                          replaceableElement.replaceWith(repeatableContent);
                        }
                      }
                    }
                    // repeating handle end
                  }
                });
              }

              node.setAttribute(
                "id",
                `${pageIndex}${sectionIndex}${ruleIndex}`
              );
              node.setAttribute("sectionname", section.name);
              node.setAttribute("rule", ruleSection.rule);
              node.setAttribute(
                "repeatingadapterid",
                ruleSection.repeatingAdapterId
              );

              if (section.locked) {
                node.setAttribute("locked", "true");
              } else {
                node.removeAttribute("locked");
              }

              if (ruleSection.isDefault) {
                node.setAttribute("defaultsection", "true");
                node.removeAttribute("hidden");
              } else {
                node.removeAttribute("defaultsection");
                node.setAttribute("hidden", true);
              }

              // add section to the page
              pageDiv.appendChild(node);
            }
          }
        });
      });
      // add new page with sections to the body
      doc.body.appendChild(pageDiv);
    });
    return doc.body;
  } catch (error) {
    console.error("Pre setup template failed : ", error);
    return doc.body.appendChild(document.createElement("div"));
  }
}

/**
 * @description - Count similar section with similar names
 * @param {Array<any>} pages - Template pages
 * @param {string} sectionName - Name of the new section
 */
export const countSimilarSections = (pages, sectionName) => {
  let count = 0;
  try {
    if (isArray(pages) && pages.length > 0) {
      pages.map((page, index) => {
        const similarSectionsCount = page.sections.filter((section) =>
          section.name.startsWith(sectionName)
        ).length;
        count = count + similarSectionsCount;
      });
    }
    return count;
  } catch (error) {
    return count;
  }
};

/**
 * @description - Handle add new section to the templated and return new template with new section
 */
export function addNewSectionToTemplate(
  newSection,
  pages,
  targetIndex,
  dropPosition,
  actionType
) {
  try {
    let rulesContent = [];
    const similarSectionsCount = countSimilarSections(pages, newSection?.name);
    const newSectionName =
      actionType == CONTEXT_MENU_ACTIONS.CUT
        ? newSection.name
        : `${newSection?.name}${
            similarSectionsCount == 0
              ? ""
              : ` ${targetIndex}${similarSectionsCount}`
          }`;
    newSection.rules?.forEach((ruleData) =>
      rulesContent.push({
        ...ruleData,
        content: updateElementPosition(ruleData?.content, dropPosition),
      })
    );

    const rules =
      actionType == CONTEXT_MENU_ACTIONS.COPY ||
      actionType == CONTEXT_MENU_ACTIONS.CUT
        ? {
            rules: rulesContent,
          }
        : {
            rules: [
              {
                ...newSection.rules[0],
                content: updateElementPosition(
                  newSection.rules[0].content,
                  dropPosition
                ),
              },
            ],
          };

    const updatedPages = pages.map((page, index) => {
      if (targetIndex === index) {
        //Change the previous section name
        const findCutIndex = page.sections?.findIndex(
          (sectionData) => sectionData.name == newSection?.name
        );

        if (findCutIndex >= 0 && actionType == CONTEXT_MENU_ACTIONS.CUT)
          page.sections[findCutIndex] = {
            ...page.sections[findCutIndex],
            name: newSectionName,
          };

        return {
          ...page,
          sections: [
            ...page.sections,
            {
              ...newSection,
              ...rules,
              name: newSectionName,
            },
          ],
        };
      } else {
        return page;
      }
    });
    return updatedPages;
  } catch (error) {
    console.error("New Section Add failed : ", error);
    return pages;
  }
}

/**
 * @description - Handle remove section from the pages and return the updated template pages
 */
export function removeSectionFromTemplate(sectionName, pages) {
  try {
    const updatedPages = pages.map((page, index) => {
      const sections = page.sections?.filter(
        (sectionData) => sectionData.name !== sectionName
      );
      page.sections = sections;
      return page;
    });
    return updatedPages;
  } catch (error) {
    console.error("removeSectionFromTemplate : ", error);
    return pages;
  }
}

/**
 * @description Update position of the dropped element
 * @param section
 * @param dropPosition
 */
const updateElementPosition = (elementContent, dropPosition) => {
  try {
    if (dropPosition && elementContent) {
      const DomParser = new DOMParser();
      const node = DomParser.parseFromString(elementContent, "text/html").body
        .firstChild;
      node.firstChild.parentElement.style.left = dropPosition.x + "px";
      node.firstChild.parentElement.style.top = dropPosition.y + "px";
      return node.firstChild.parentElement.outerHTML;
    } else {
      return elementContent;
    }
  } catch (error) {
    return elementContent;
  }
};

// /**
//  * @description - Handle add new section to the templated and return new template with new section
//  */
// export function removeSectionFromTemplate(name, pageId) {
//   try {
//     const pageElement = document.getElementById(pageId);
//     const childNodesToRemove = pageElement?.querySelectorAll(
//       `div[sectionname="${name}"]`
//     );
//     if (childNodesToRemove && childNodesToRemove.length > 0) {
//       childNodesToRemove.forEach((childNode) =>
//         childNode.parentNode.removeChild(childNode)
//       );
//     }
//   } catch (error) {
//     console.error("Remove Section failed : ", error);
//   }
// }

/**
 * @description - Download template as a json file
 * @param jsonData - JSON data object
 * @param fileName - a name for the file
 */
export function downloadJsonFile(jsonData, fileName) {
  try {
    const jsonString = `data:text/json;chatset=utf-8,${encodeURIComponent(
      jsonData
    )}`;
    const link = document.createElement("a");
    link.href = jsonString;
    link.download = fileName
      ? `${fileName}.json`
      : `template_${getTimestampInSeconds()}.json`;

    link.click();
  } catch (error) {
    console.error(error);
  }
}

/**
 * @description - Get current timestamp in seconds
 */
function getTimestampInSeconds() {
  return Math.floor(Date.now() / 1000);
}

/**
 * @description - Remove Zero width spaces and trim the HTML string
 * @sectionId - HTML content string
 */
export const trimSpaces = (string) => {
  try {
    return string.replace(/[\u200B-\u200D\uFEFF]/g, "");
    // .replace(/&nbsp;/g, "")
    // .replace(/&amp;/g, "&")
    // .replace(/&gt;/g, ">")
    // .replace(/&lt;/g, "<");
  } catch (error) {
    console.log("trimSpaces failed : ", error);
  }
};

/**
 * @description - Returns status of the current template changes
 * @param type - Navigation or tab close
 * @param pagesElement - initial template data
 * @param initDocumentData - initial template data
 */
export const getEditStatus = (pagesElement, initDocumentData, type) => {
  try {
    const templateJson = document?.getElementById("pages-container");
    if (type === "navigate") {
      if (templateJson && pagesElement) {
        return pagesElement !== templateJson.outerHTML;
      } else {
        return false;
      }
    } else {
      if (templateJson && initDocumentData.current) {
        return initDocumentData.current !== templateJson.outerHTML;
      } else {
        return false;
      }
    }
  } catch (e) {
    return false;
  }
};
